/**
 * CloneDataTemplateDialog
 * Modal dialog for cloning a DataTemplate record.
 * Accepts a source DataTemplateID and an optional proposed name.
 * On success, fires onCloned with the new record.
 */

import React, { useState, useRef, useEffect } from 'react';
import { DataTemplateApiClient } from '../ApiClient/DataTemplateApiClient';
import type { DataTemplateRecord } from '../types';

const apiClient = new DataTemplateApiClient();

export interface CloneDataTemplateDialogProps {
  /** ID of the template to clone */
  sourceDataTemplateID: number;
  /** Display name of the source (shown in the dialog for context) */
  sourceName: string;
  /** Called with the new record after a successful clone */
  onCloned?: (newRecord: DataTemplateRecord) => void;
  /** Called when the user cancels or after a successful clone */
  onClose: () => void;
}

export const CloneDataTemplateDialog: React.FC<CloneDataTemplateDialogProps> = ({
  sourceDataTemplateID,
  sourceName,
  onCloned,
  onClose,
}) => {
  const [proposedName, setProposedName] = useState('');
  const [cloning, setCloning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cloned, setCloned] = useState(false);
  const [newRecord, setNewRecord] = useState<DataTemplateRecord | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus the name input when the dialog opens
    setTimeout(() => inputRef.current?.focus(), 60);
  }, []);

  const handleClone = async () => {
    setCloning(true);
    setError(null);

    const result = await apiClient.clone(sourceDataTemplateID, proposedName.trim() || undefined);

    if (result.success) {
      setCloned(true);
      setNewRecord(result.newRecord ?? null);
      if (result.newRecord) onCloned?.(result.newRecord);
    } else {
      setError(result.error ?? 'Clone failed.');
    }
    setCloning(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !cloning && !cloned) handleClone();
    if (e.key === 'Escape') onClose();
  };

  const defaultName = `Clone of ${sourceName}`;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'auto',
      }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#0f172a',
          border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: '10px',
          width: '440px',
          padding: '24px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.8)',
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
            Clone Template
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', wordBreak: 'break-word' }}>
            Creating a copy of <strong style={{ color: '#94a3b8' }}>{sourceName}</strong>
          </div>
          <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>
            ID: {sourceDataTemplateID}
          </div>
        </div>

        {!cloned ? (
          <>
            {/* Name input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>
                New Template Name
              </label>
              <input
                ref={inputRef}
                type="text"
                value={proposedName}
                onChange={e => setProposedName(e.target.value)}
                placeholder={defaultName}
                maxLength={200}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: '6px',
                  color: '#e2e8f0',
                  fontSize: '13px',
                  padding: '8px 10px',
                  outline: 'none',
                }}
              />
              <div style={{ fontSize: '11px', color: '#475569', marginTop: '5px' }}>
                Leave blank to use "{defaultName}"
              </div>
            </div>

            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: '6px', padding: '10px 12px', color: '#fca5a5', fontSize: '12px', marginBottom: '16px',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '7px 18px', borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.15)', background: 'transparent',
                  color: 'rgba(255,255,255,0.7)', fontSize: '12px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleClone}
                disabled={cloning}
                style={{
                  padding: '7px 22px', borderRadius: '6px', border: 'none',
                  background: cloning ? '#374151' : '#309B48',
                  color: '#fff', fontSize: '12px', fontWeight: 600,
                  cursor: cloning ? 'not-allowed' : 'pointer',
                }}
              >
                {cloning ? 'Cloning…' : 'Clone'}
              </button>
            </div>
          </>
        ) : (
          /* Success state */
          <>
            <div style={{
              background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
              borderRadius: '8px', padding: '16px', marginBottom: '20px',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#22c55e', marginBottom: '6px' }}>
                Template cloned successfully
              </div>
              {newRecord && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                  <div><span style={{ color: '#64748b' }}>Name:</span> {newRecord.templateName}</div>
                  <div><span style={{ color: '#64748b' }}>ID:</span> {newRecord.dataTemplateID}</div>
                  <div style={{ marginTop: '6px', fontSize: '11px', color: '#64748b' }}>
                    The clone starts as disabled and unpublished.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '7px 22px', borderRadius: '6px', border: 'none',
                  background: '#309B48', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
